import React, { useEffect, useState } from 'react';
import '../styles/MemberDetails.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SideBar from '../components/SideBar';
import '../styles/WalletPage.css';
import noRecordsImg from "../assets/images/no-record-found 3.png";
import '../styles/CustomerUnitsTransactionHistory.css';
import { customerService, resolveCustomerRecord } from '../services/customerServices';
import { retailerService, resolveRetailRecord } from '../services/RetailerServices';

const buildDistributionRows = (distribution = [], summaryRows = []) => {
    const rows = [...distribution];
    const insertAfter = rows.findIndex((item) => item.entity_type === 'customer_mini_agent');
    const insertAt = insertAfter >= 0 ? insertAfter + 1 : rows.length;

    return [
        ...rows.slice(0, insertAt),
        ...summaryRows,
        ...rows.slice(insertAt),
    ];
};

const formatDistributionTopcoin = (item) => {
    if (!item.is_summary_row) {
        return item.topcoin > 0 ? `+${item.topcoin}` : '—';
    }

    switch (item.entity_type) {
        case 'extra_cards_promotion':
        case 'user_topcoin_earned':
            return item.topcoin > 0 ? `+${item.topcoin}` : '0';
        case 'mtopcoin':
        case 'total_company_topcoin':
            return String(item.topcoin ?? 0);
        default:
            return '—';
    }
};

const formatDistributionBonus = (item) => {
    if (!item.is_summary_row) {
        return item.bonus_gbp > 0 ? `£${item.bonus_gbp.toFixed(4)}` : '—';
    }

    if (item.entity_type === 'bank_cash_wallet' || item.entity_type === 'total_transaction_amount') {
        return `£${Number(item.bonus_gbp || 0).toFixed(2)}`;
    }

    return '—';
};

function CustomerSalesHistory() {
    const location = useLocation();
    const agent = location.state?.agent;
    const retail = resolveRetailRecord(location.state?.retail);
    const fromRetail = Boolean(location.state?.fromRetail);
    const fromAgent = !fromRetail && Boolean(location.state?.fromAgent || agent);
    const customer = fromRetail ? null : resolveCustomerRecord(location.state?.customer || agent);
    const navigate = useNavigate();

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({});
    const [summary, setSummary] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 20;

    // Distribution expansion state
    const [expandedSbcRef, setExpandedSbcRef] = useState(null);
    const [distributionData, setDistributionData] = useState(null);
    const [distributionLoading, setDistributionLoading] = useState(false);

    const goToWallet = () => {
        if (fromRetail) {
            navigate('/retail-wallet-details', { state: { retail } });
            return;
        }
        if (fromAgent) {
            navigate('/advertising-agent-wallet-details', { state: { agent: agent || customer } });
            return;
        }
        navigate('/customer-wallet-details', { state: { customer } });
    };
    const goToRequestHistory = () => {
        if (fromRetail) {
            navigate('/retail-cash-request-history', { state: { retail } });
            return;
        }
        if (fromAgent) {
            navigate('/advertising-agent-request-history', { state: { agent: agent || customer } });
            return;
        }
        navigate('/customer-cash-request-history', { state: { customer } });
    };
    const goToDetails = () => {
        if (fromRetail) {
            navigate('/retailer-shop-details', { state: { retail } });
            return;
        }
        if (fromAgent) {
            navigate('/advertising-agent-details', { state: { agent: agent || customer } });
            return;
        }
        navigate('/customer-details', { state: { customer } });
    };

    const fetchSalesHistory = async (page = 1) => {
        const entityId = fromRetail
            ? (retail?.id || retail?.retail_business_id || retail?.retailer_id)
            : (customer?.id || customer?.Customer_id || customer?.customer_id);

        if (!entityId) {
            console.error(fromRetail ? 'No retail business ID found:' : 'No customer ID found:', fromRetail ? retail : customer);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const params = { page, per_page: rowsPerPage };
            const response = fromRetail
                ? await retailerService.getRetailerSalesHistory(entityId, params)
                : await customerService.getCustomerSalesHistory(entityId, params);

            setSales(response.sales || []);
            setPagination(response.pagination || {});
            setSummary(response.summary || {});
        } catch (err) {
            console.error('API Error:', err);
            setError(err.message || 'Failed to fetch sales history');
        } finally {
            setLoading(false);
        }
    };

    const fetchDistribution = async (sbcRef) => {
        if (expandedSbcRef === sbcRef) {
            // Collapse if already expanded
            setExpandedSbcRef(null);
            setDistributionData(null);
            return;
        }

        setExpandedSbcRef(sbcRef);
        setDistributionLoading(true);
        setDistributionData(null);

        try {
            const response = await customerService.getSalesDistribution(sbcRef);
            setDistributionData(response);
        } catch (err) {
            console.error('Distribution fetch error:', err);
            setDistributionData({ error: err.message || 'Failed to load distribution' });
        } finally {
            setDistributionLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        fetchSalesHistory(1);
    }, []);

    useEffect(() => {
        if (currentPage > 1) {
            fetchSalesHistory(currentPage);
        }
    }, [currentPage]);

    if (fromRetail && !retail) return <p>No retail business selected.</p>;
    if (!fromRetail && !customer) return <p>No customer selected.</p>;

    const partyColumnLabel = fromRetail ? 'Customer' : 'Retail Business';
    const getSalePartyName = (sale) => fromRetail ? sale.customer_name : sale.retail_business_name;
    const getSalePartyEmail = (sale) => fromRetail ? sale.customer_email : sale.retail_business_email;
    const pageTitle = fromRetail
        ? 'Retailer Shop Details'
        : (fromAgent ? 'Advertising Agent Details' : 'Customer Details');
    const emptyMessage = fromRetail
        ? 'No customer purchase transactions found for this retail business.'
        : 'No retail business purchase transactions found for this customer.';

    const distributionRows = distributionData?.distribution
        ? buildDistributionRows(distributionData.distribution, distributionData.summary_rows)
        : [];
    const bonusCardPromotions = (distributionData?.card_promotions || [])
        .filter((promo) => promo.wallet_type !== 'Extra Card Promotion');

    return (
        <div className="member-details-layout">
            <SideBar />
            <div className="member-details-main">
                {/* Header */}
                <div className="details-header">
                    <h2>{pageTitle}</h2>
                    <p>Retail business sales transaction history</p>
                </div>

                {/* Breadcrumb */}
                <div className="breadcrumb">
                    <Link to="/dashboard" className="breadcrumb-link">Home</Link>
                    <span className="breadcrumb-separator">{">"}</span>
                    {fromRetail ? (
                        <>
                            <Link to="/manage-retailer" className="breadcrumb-link">Manage Retailer Shop</Link>
                            <span className="breadcrumb-separator">{">"}</span>
                        </>
                    ) : fromAgent ? (
                        <>
                            <Link to="/manage-advertising-agent" className="breadcrumb-link">Manage Ads Agents</Link>
                            <span className="breadcrumb-separator">{">"}</span>
                        </>
                    ) : (
                        <>
                            <Link to="/manage-customer" className="breadcrumb-link">Manage Customer</Link>
                            <span className="breadcrumb-separator">{">"}</span>
                        </>
                    )}
                    <span className="breadcrumb-current">Retail Sales History</span>
                </div>

                {/* Top buttons */}
                <div className="top-bar">
                    <div className="top-buttons">
                        <button className="tab-btn" onClick={goToDetails}>Personal Details</button>
                        <button className="tab-btn active" onClick={goToWallet}>Wallet Details</button>
                        <button className="tab-btn" onClick={goToRequestHistory}>
                            {fromAgent ? 'Request Details' : 'Cash Request History'}
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                {!loading && (summary.total_transactions > 0) && (
                    <div className="wallet-section" style={{ marginBottom: '0' }}>
                        <div className="wallet-card" style={{ flex: 1 }}>
                            <h3>Total Purchases</h3>
                            <h2 className="balance-value">{summary.total_transactions || 0}</h2>
                        </div>
                        <div className="wallet-card" style={{ flex: 1 }}>
                            <h3>Total Spend (£)</h3>
                            <h2 className="balance-value">£{Number(summary.total_purchase_amount || 0).toFixed(2)}</h2>
                        </div>
                        <div className="wallet-card" style={{ flex: 1 }}>
                            <h3>Total TopCoin Awarded</h3>
                            <h2 className="balance-value">{Number(summary.total_topcoin_awarded || 0).toLocaleString()} TC</h2>
                        </div>
                    </div>
                )}

                {/* Sales Transaction Section */}
                <div className="transaction-container">
                    <div className="transaction-header">
                        <h2 className="title">Retail Sales History</h2>
                        <div className="transaction-count">
                            {pagination.total || 0} {pagination.total === 1 ? "Transaction" : "Transactions"}
                        </div>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <div className="transaction-content">
                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Loading sales history...</p>
                            </div>
                        ) : sales.length > 0 ? (
                            <>
                                <div className="transaction-table-container">
                                    <div className="table-header">
                                        <div className="cell date">Date</div>
                                        <div className="cell description">{partyColumnLabel}</div>
                                        <div className="cell email">Email</div>
                                        <div className="cell type">Invoice No</div>
                                        <div className="cell units">Purchase (£)</div>
                                        <div className="cell units">TopCoin</div>
                                        <div className="cell description">SBC Ref</div>
                                    </div>
                                    <div className="table-body">
                                        {sales.map((sale) => (
                                            <React.Fragment key={sale.id}>
                                                <div
                                                    className={`table-row ${expandedSbcRef === sale.sbc_ref ? 'expanded' : ''}`}
                                                    onClick={() => fetchDistribution(sale.sbc_ref)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to view distribution details"
                                                >
                                                    <div className="cell date" data-label="Date">
                                                        {sale.transaction_datetime
                                                            ? new Date(sale.transaction_datetime).toLocaleDateString("en-GB", {
                                                                year: "numeric",
                                                                month: "short",
                                                                day: "numeric",
                                                              })
                                                            : "N/A"}
                                                    </div>
                                                    <div className="cell description" data-label={partyColumnLabel}>
                                                        {getSalePartyName(sale)}
                                                    </div>
                                                    <div className="cell email" data-label="Email">
                                                        {getSalePartyEmail(sale) || '—'}
                                                    </div>
                                                    <div className="cell type" data-label="Invoice No">
                                                        <span className="type-badge credit">
                                                            {sale.invoice_no || 'N/A'}
                                                        </span>
                                                    </div>
                                                    <div className="cell units" data-label="Purchase (£)">
                                                        £{Number(sale.transaction_total).toFixed(2)}
                                                    </div>
                                                    <div className="cell units positive" data-label="TopCoin">
                                                        +{sale.awarding_topcoin} TC
                                                        {sale.manual_override_applied && (
                                                            <span title={`Override: ${sale.manual_override_reason || 'No reason'}`}
                                                                  style={{ marginLeft: '4px', color: '#f5a623', fontSize: '12px' }}>⚠</span>
                                                        )}
                                                    </div>
                                                    <div className="cell description" data-label="SBC Ref"
                                                         style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                                                        {expandedSbcRef === sale.sbc_ref ? '▼' : '▶'} {sale.sbc_ref}
                                                    </div>
                                                </div>

                                                {/* Distribution Expansion */}
                                                {expandedSbcRef === sale.sbc_ref && (
                                                    <div className="distribution-panel" style={{
                                                        background: 'rgba(10, 25, 60, 0.6)',
                                                        border: '1px solid rgba(212, 175, 55, 0.3)',
                                                        borderRadius: '8px',
                                                        margin: '4px 0 12px 0',
                                                        padding: '16px',
                                                        animation: 'fadeIn 0.3s ease-in-out',
                                                    }}>
                                                        {distributionLoading ? (
                                                            <div style={{ textAlign: 'center', padding: '20px', color: '#ccc' }}>
                                                                <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
                                                                <p>Loading distribution details...</p>
                                                            </div>
                                                        ) : distributionData?.error ? (
                                                            <div style={{ color: '#ff6b6b', textAlign: 'center', padding: '10px' }}>
                                                                {distributionData.error}
                                                            </div>
                                                        ) : distributionData?.distribution ? (
                                                            <>
                                                                <h4 style={{
                                                                    color: '#d4af37',
                                                                    margin: '0 0 12px 0',
                                                                    fontSize: '14px',
                                                                    borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
                                                                    paddingBottom: '8px',
                                                                }}>
                                                                    TopCoin Distribution Breakdown
                                                                </h4>

                                                                <table style={{
                                                                    width: '100%',
                                                                    borderCollapse: 'collapse',
                                                                    fontSize: '13px',
                                                                }}>
                                                                    <thead>
                                                                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                                                                            <th style={{ textAlign: 'left', padding: '8px', color: '#d4af37', fontWeight: '600' }}>Entity</th>
                                                                            <th style={{ textAlign: 'left', padding: '8px', color: '#d4af37', fontWeight: '600' }}>Name</th>
                                                                            <th style={{ textAlign: 'left', padding: '8px', color: '#d4af37', fontWeight: '600' }}>Email</th>
                                                                            <th style={{ textAlign: 'right', padding: '8px', color: '#d4af37', fontWeight: '600' }}>TopCoin</th>
                                                                            <th style={{ textAlign: 'right', padding: '8px', color: '#d4af37', fontWeight: '600' }}>Bonus (£)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {distributionRows.map((item, idx) => (
                                                                            <tr key={`${item.entity_type}-${idx}`} style={{
                                                                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                                                                background: item.is_summary_row ? 'rgba(212, 175, 55, 0.05)' : 'transparent',
                                                                            }}>
                                                                                <td style={{ padding: '7px 8px', color: '#e0e0e0' }}>
                                                                                    {item.entity_label}
                                                                                </td>
                                                                                <td style={{ padding: '7px 8px', color: '#aaa' }}>
                                                                                    {item.name || '—'}
                                                                                </td>
                                                                                <td style={{ padding: '7px 8px', color: '#b6b3b3', wordBreak: 'break-all' }}>
                                                                                    {item.email || '—'}
                                                                                </td>
                                                                                <td style={{ padding: '7px 8px', color: '#4ecdc4', textAlign: 'right', fontWeight: '500' }}>
                                                                                    {formatDistributionTopcoin(item)}
                                                                                </td>
                                                                                <td style={{ padding: '7px 8px', color: '#f5a623', textAlign: 'right', fontWeight: '500' }}>
                                                                                    {formatDistributionBonus(item)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>

                                                                {/* Card Promotions */}
                                                                {bonusCardPromotions.length > 0 && (
                                                                    <>
                                                                        <h4 style={{
                                                                            color: '#d4af37',
                                                                            margin: '16px 0 8px 0',
                                                                            fontSize: '13px',
                                                                            borderTop: '1px solid rgba(212, 175, 55, 0.2)',
                                                                            paddingTop: '12px',
                                                                        }}>
                                                                            Card Promotion Credits
                                                                        </h4>
                                                                        {bonusCardPromotions.map((promo, idx) => (
                                                                            <div key={idx} style={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                padding: '6px 8px',
                                                                                color: '#e0e0e0',
                                                                                fontSize: '13px',
                                                                            }}>
                                                                                <span>{promo.wallet_type}</span>
                                                                                <span style={{ color: '#4ecdc4', fontWeight: '500' }}>
                                                                                    +{promo.amount} TC
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>

                                {/* Pagination */}
                                {pagination.last_page > 1 && (
                                    <div className="transaction-pagination">
                                        <button
                                            className="page-btn"
                                            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                            disabled={currentPage === 1 || loading}
                                        >
                                            &lt;
                                        </button>

                                        {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                            let pageNum;
                                            if (pagination.last_page <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= pagination.last_page - 2) {
                                                pageNum = pagination.last_page - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`page-btn ${currentPage === pageNum ? "active" : ""}`}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    disabled={loading}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            className="page-btn"
                                            onClick={() => setCurrentPage(p => Math.min(p + 1, pagination.last_page))}
                                            disabled={currentPage === pagination.last_page || loading}
                                        >
                                            &gt;
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-records">
                                <img src={noRecordsImg} alt="No records" />
                                <h3>No retail sales found</h3>
                                <p>{emptyMessage}</p>
                            </div>
                        )}
                    </div>

                    <div className="back-link">
                        <Link
                            to={fromRetail ? '/retail-wallet-details' : (fromAgent ? '/advertising-agent-wallet-details' : '/customer-wallet-details')}
                            state={fromRetail ? { retail } : (fromAgent ? { agent: agent || customer } : { customer })}
                        >
                            Back to Wallet &lt;&lt;
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CustomerSalesHistory;